import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../Common/hooks/useToast";
import { Consultation, ClinicalNotes, Prescription, PatientInfo } from "../../../Common/interfaces/doctor.interfaces";
import { URLS } from "../../../Common/axios_instance/axios_instance";
import {
  fetchConsultationDetail,
  fetchConsultationNotes,
  fetchConsultationPrescription,
  fetchPatientInfo,
  fetchPrescriptionPdf,
  fetchClinicalNotesFileUrl,
  shareConsultationData,
} from "../https/consultation_detail_module.https";

export const useConsultationDetailModule = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [notes, setNotes] = useState<ClinicalNotes | null>(null);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (!id) return;

    fetchConsultationDetail(id)
      .then((c) => {
        if (c?.data?.data) setConsultation(c.data.data);
      })
      .catch((err) => console.error("Error fetching consultation detail:", err));

    fetchConsultationNotes(id)
      .then((n) => {
        if (n?.data?.data) setNotes(n.data.data);
      })
      .catch((err) => console.error("Error fetching notes:", err));

    fetchConsultationPrescription(id)
      .then((rx) => {
        if (rx?.data?.data) setPrescription(rx.data.data);
      })
      .catch((err) => console.error("Error fetching prescription:", err));

    setLoadingPatient(true);
    fetchPatientInfo(id)
      .then((p) => {
        if (p?.data?.data) setPatientInfo(p.data.data);
      })
      .catch((err) => console.error("Error fetching patient info:", err))
      .finally(() => setLoadingPatient(false));
  }, [id]);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min${secs > 0 ? ` ${secs} sec` : ""}`;
  };

  const handleShare = async (channel: string) => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      await shareConsultationData(id!, [channel]);
      showToast("Prescription sent successfully", "success");
      setShowShareSheet(false);
    } catch (e: any) {
      showToast(e.response?.data?.message || "Failed to share", "error");
    } finally {
      setIsSharing(false);
    }
  };

  const handleViewUploadedPrescription = async () => {
    if (!id) return;
    try {
      showToast("Generating secure view URL...", "success");
      const res = await fetchPrescriptionPdf(id);
      const viewUrlRaw = res.data?.url;
      if (!viewUrlRaw) {
        showToast("No prescription PDF generated yet", "error");
        return;
      }
      
      let viewUrl = viewUrlRaw;
      if (!viewUrlRaw.startsWith("http")) {
        const cleanPath = viewUrlRaw.startsWith("/") ? viewUrlRaw.slice(1) : viewUrlRaw;
        const parts = cleanPath.split("/");
        if (parts.length >= 3) {
          const docName = parts.pop();
          const docFolder = parts.pop();
          const benId = parts.pop();
          viewUrl = `${URLS.AZURE_URI}/signedUrl/get-document/${benId}/${docFolder}/${docName}`;
        } else {
          viewUrl = `${URLS.AZURE_URI}${viewUrlRaw}`;
        }
      }
      
      window.open(viewUrl, "_blank");
    } catch (e: any) {
      showToast(e.response?.data?.message || "Failed to open prescription", "error");
    }
  };

  const handleViewClinicalNotesFile = async () => {
    if (!id) return;
    try {
      showToast("Generating secure view URL...", "success");
      const res = await fetchClinicalNotesFileUrl(id);
      const viewUrlRaw = res.data?.url;
      if (!viewUrlRaw) {
        showToast("No clinical notes file generated yet", "error");
        return;
      }
      
      let viewUrl = viewUrlRaw;
      if (!viewUrlRaw.startsWith("http")) {
        const cleanPath = viewUrlRaw.startsWith("/") ? viewUrlRaw.slice(1) : viewUrlRaw;
        const parts = cleanPath.split("/");
        if (parts.length >= 3) {
          const docName = parts.pop();
          const docFolder = parts.pop();
          const benId = parts.pop();
          viewUrl = `${URLS.AZURE_URI}/signedUrl/get-document/${benId}/${docFolder}/${docName}`;
        } else {
          viewUrl = `${URLS.AZURE_URI}${viewUrlRaw}`;
        }
      }
      
      window.open(viewUrl, "_blank");
    } catch (e: any) {
      showToast(e.response?.data?.message || "Failed to open clinical notes file", "error");
    }
  };

  const hasShareableContent =
    (notes && (notes.chief_complaint || notes.provisional_diagnosis || notes.symptoms?.length)) ||
    (prescription?.medicines && prescription.medicines.length > 0) ||
    !!(prescription?.prescription?.prescription_pdf_url);

  // Show rejoin button if consultation is ongoing/accepted/completed and has room_id
  const showRejoin = (() => {
    if (!consultation) return false;
    const rejoiningStatuses = ["ongoing", "accepted", "completed"];
    return rejoiningStatuses.includes(consultation.status) && !!consultation.room_id;
  })();

  // Check if the ACS room is expired (more than 1 hour elapsed since the most recent activity)
  const isRejoinExpired = (() => {
    if (!consultation) return true;
    const referenceTime =
      consultation.ended_at ||
      consultation.accepted_at ||
      consultation.started_at ||
      consultation.requested_at;
    if (!referenceTime) return true;
    const elapsed = Date.now() - new Date(referenceTime).getTime();
    return elapsed >= 60 * 60 * 1000; // expired if 1 hour or more has passed
  })();

  const initials = consultation?.patient_name
    ? consultation.patient_name.slice(0, 2).toUpperCase()
    : "PT";

  return {
    id,
    consultation,
    notes,
    prescription,
    patientInfo,
    loadingPatient,
    showShareSheet,
    isSharing,
    initials,
    hasShareableContent,
    showRejoin,
    isRejoinExpired,
    formatDuration,
    handleShare,
    handleViewUploadedPrescription,
    handleViewClinicalNotesFile,
    setShowShareSheet,
    navigate,
    toast,
  };
};
